<script setup lang="ts">
import {computed, onBeforeMount, ref} from 'vue';
import axios from "axios";
import ApiHelper, {API_HELPERS} from "@/helpers/apiHelper.ts";
import {useContestStore} from "@/stores/contest.ts";
import { storeToRefs } from "pinia";
import AuthService from "@/services/authService.ts";
import { VueRecaptcha } from "vue-recaptcha";

const theme = ref<'light' | 'dark'>('light'),
    captchaResponse = ref(),
    responseMessage = ref(''),
    sendingForm = ref(false),
    errors = ref<any>({}),
    backendErrors = ref<any>({}),
    recaptchaRef = ref<InstanceType<typeof VueRecaptcha> | null>(null);

const autService = new AuthService();
const contestStore = useContestStore(),
     {contestInfo} = storeToRefs(contestStore);

const verificationCode = ref(''),
    email = ref(''),
    phone = ref(''),
    declaration_of_adulthood = ref(false),
    acceptance_of_the_regulations = ref(false),
    declaration_of_fulfilment_regulations_conditions = ref(false),
    confirmation_of_data_processing_knowledge = ref(false),
    data_processing_consent = ref(false);

const phoneValidation = (phone: string) => {
    const regex = /^(?:(?:(?:(?:\+|00)\d{2})?[ -]?(?:(?:\(0?\d{2}\))|(?:0?\d{2})))?[ -]?(?:\d{3}[- ]?\d{2}[- ]?\d{2}|\d{2}[- ]?\d{2}[- ]?\d{3}|\d{7})|(?:(?:(?:\+|00)\d{2})?[ -]?\d{3}[ -]?\d{3}[ -]?\d{3}))$/;
    return regex.test(phone);
}

const makeSubmission = async () => {
    errors.value = {};
    if (phone.value != '' && !phoneValidation(phone.value)) {
        errors.value['phone'] = 'error';
    }
    if (!captchaResponse.value) {
        errors.value['captcha'] = 'error';
    }

    if (!Object.keys(errors.value).length) {
        const data = {
            'g-recaptcha-response': captchaResponse.value,
            'email': email.value,
            'phone': phone.value,
            'verification_code': verificationCode.value,
            'payload': JSON.stringify({
                'declaration_of_adulthood': declaration_of_adulthood.value,
                'acceptance_of_the_regulations': acceptance_of_the_regulations.value,
                'declaration_of_fulfilment_regulations_conditions': declaration_of_fulfilment_regulations_conditions.value,
                'confirmation_of_data_processing_knowledge': confirmation_of_data_processing_knowledge.value,
                'data_processing_consent': data_processing_consent.value,
            }),
            'contest_id': API_HELPERS.contestId,
        };
        sendingForm.value = true;
        const token = await autService.getToken();
        await axios.post(ApiHelper.getSubmissionCreateUrl(), data, {
            headers: {'X-Form-Token': token}
        })
            .then(response => {
                errors.value = {}
                backendErrors.value = {}
                responseMessage.value = '';
                sendingForm.value = false;

                if (response.status == 200 && response.data.submission_id) {
                    responseMessage.value = 'Formularz został wysłany';
                    const submissionId = response.data.submission_id;
                    window.location.href = import.meta.env.VITE_GAME_URL + '?token=' + submissionId;
                } else {
                    responseMessage.value = 'Podczas wysyłania formularza wystąpił błąd.';
                }
            }).catch(error => {
                if (error.response?.status == 422 && error.response?.data?.errors) {
                    backendErrors.value = error.response.data.errors;
                    errors.value = {}
                    recaptchaRef.value?.reset();
                } else if (error.response.status == 412) {
                    captchaResponse.value = '';
                    backendErrors.value['captcha'] = 'error';
                    errors.value = {}
                    recaptchaRef.value?.reset();
                } else {
                    errors.value = {}
                    backendErrors.value = {}
                    responseMessage.value = 'Podczas wysyłania formularza wystąpił błąd.';
                    recaptchaRef.value?.reset();
                }

              sendingForm.value = false;

            });
    }
}

const onCaptchaVerify = (token: string) => {
    captchaResponse.value = token;
}

onBeforeMount(async () => {
    await contestStore.getContestInfo();
})
</script>

<template>
    <div id="submission" class="p-4 bg-kozel-brown">
        <h1 class="text-5xl text-kozel-white text-center pb-4">Weź udział</h1>
        <template v-if="!contestInfo?.submission_available">
            <div class="text-center text-kozel-sandy info-box">
                <template v-if="contestInfo.submission_ended">
                    Przyjmowanie zgłoszeń w konkursie zostało zakończone.<br/>
                    Dziękujemy za udział!
                </template>
                <template v-else>
                    Przyjmowanie zgłoszeń wkrótce się rozpocznie.
                </template>
            </div>
        </template>
        <template v-else>
            <div v-show="responseMessage" class="message-box text-center text-kozel-sandy text-sm">{{ responseMessage }}</div>
            <form class="pt-4" @submit.prevent="makeSubmission">
                <div class="pr-4 flex flex-col gap-4">
                    <div>
                        <input class="block w-full px-1 py-2.5 pl-2.5"
                               id="code"
                               v-model="verificationCode"
                               placeholder="Kod z aplikacji Żappka"
                               aria-placeholder="Kod z aplikacji Żappka"
                               required
                        />
                        <div v-show="(errors && errors.verification_code) || (backendErrors && backendErrors.verification_code)" class="pt-2 text-kozel-sandy error-label">Podaj prawidłowy kod z aplikacji Żappka</div>
                    </div>
                    <div>
                        <input class="block w-full px-1 py-2.5 pl-2.5"
                               id="email"
                               v-model="email"
                               type="email"
                               placeholder="Adres e-mail"
                               aria-placeholder="Adres e-mail"
                               required
                        />
                        <label v-show="(errors && errors.email) || (backendErrors && backendErrors.email)" class="pt-2 text-kozel-sandy error-label">Podaj prawidłowy adres e-mail</label>
                    </div>
                    <div>
                        <input class="block w-full px-1 py-2.5 pl-2.5"
                               id="phone"
                               v-model="phone"
                               max="20"
                               placeholder="Numer telefonu (niobowiązkowy)"
                               aria-placeholder="Numer telefonu (niobowiązkowy)"
                        />
                        <label v-show="(errors && errors.phone) || (backendErrors && backendErrors.phone)" class="pt-2 text-kozel-sandy error-label">Podaj prawidłowy numer telefonu</label>
                    </div>
                </div>

                <div class="flex flex-col gap-4 pt-4">
                    <div class="regulations-items">Informacje o przetwarzaniu danych osobowych</div>
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_1"
                               v-model="declaration_of_adulthood"
                               class="w-4 h-4 bg-kozel-white shrink-0"
                               required
                        />
                        <label for="regulation_1" class="text-kozel-white">* Oświadczam, że mam ukończone 18 lat.</label>
                    </div>
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_2"
                               v-model="acceptance_of_the_regulations"
                               class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                               required
                        />
                        <label for="regulation_2" class="text-kozel-white">* Oświadczam, że Regulamin Konkursu "WYZWANIE KOZLA" jest mi znany i&nbsp;akceptuję jego treść.</label>
                    </div>
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_3"
                               v-model="declaration_of_fulfilment_regulations_conditions"
                               class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                               required
                        />
                        <label for="regulation_3" class="text-kozel-white">* Oświadczam, że spełniam warunki przewidziane w&nbsp;&#167;2 Regulamin Konkursu.</label>
                    </div>
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_4"
                               v-model="confirmation_of_data_processing_knowledge"
                               class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                               required
                        />
                        <label for="regulation_4" class="text-kozel-white">* Oświadczam, iż zostałem poinformowany o&nbsp;zasadach przetwarzania moich danych osobowych, określonych Regulaminem Konkursu "WYZWANIE KOZLA".</label>
                    </div>
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_5"
                               v-model="data_processing_consent"
                               class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                        />
                        <label for="regulation_5" class="text-kozel-white">
                            Wyrażam zgodę na przetwarzanie moich danych osobowych
                            (imię, nazwisko, adres e-mai) w celu otrzymywania
                            wiadomości o&nbsp;charakterze marketingowym, dotyczących
                            marek Kompanii Piwowarskiej&nbsp;S.A. z&nbsp;siedzibą w&nbsp;Poznaniu,
                            która jest administratorem danych osobowych
                            przetwarzanych w&nbsp;tym celu. Szczegóły dotyczące
                            przetwarzania danych osobowych znajdują się w&nbsp;Regulaminie.
                        </label>
                    </div>
                    <div class="regulations-items pt-2">*Oświadczenie jest obowiązkowe</div>
                </div>
                <div class="regulations-items py-6">
                    <div class="flex flex-col justify-center items-center gap-1">
                        <VueRecaptcha ref="recaptchaRef"
                                      sitekey="6Lfs7OAqAAAAAK4QYfveyBPCwnc0A6rwj5IkSh0a"
                                      @verify="onCaptchaVerify">
                        </VueRecaptcha>
                        <span
                            v-if="backendErrors['captcha']"
                          class="text-kozel-sandy error-label"
                        >
                            Potwierdź, że nie jesteś robotem.
                        </span>
                    </div>
                </div>
              <button class="block" :disabled="sendingForm">
                <span v-if="!sendingForm">WEJDŹ DO GRY</span>
                <span v-else class="animate__animated animate__fadeIn">WYSYŁANIE...</span>
              </button>
            </form>
        </template>
    </div>
</template>

<style scoped>
input {
    background: var(--color-kozel-white);
    font-size: 0.8rem;
}

input::placeholder, label, input, .regulations-items, .message-box, .error-label {
    font-family: 'ApexSerif', 'serif';
    font-weight: 700;
}

input::placeholder {
    color: var(--color-kozel-sandy);
}

.regulations-items {
    color: var(--color-kozel-white);
    font-size: 0.6rem;
}

label, .error-label {
    font-size: 0.6rem;
}

.info-box {
    font-family: 'ApexSerif', 'serif';
    font-weight: 400;
}
</style>
